import { Button, Card, CardContent, Grid, Stack, CardActions, CircularProgress, Typography } from "@mui/material";
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import { StepHeader, stepCardSx, stepActionsSx, backBtnSx, nextBtnSx } from "./stepUi";
import { useSelector, useDispatch } from "react-redux";
import { setActiveBack } from "../campaignSlice";
import { FieldSelect } from "../../../components/selections/fieldInputs";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import DateTimePickerField from "../../../components/dateTimePickerField/dateTimePickerField";
import { setScheduleDate } from "../campaignSlice";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import { createCampaignThunk, getCampaignsThunk } from "../campaignThunk";
import { APP_ROUTES } from "../../../config/appRoutes";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";

export default function ScheduleCampaign() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { activeStep, scheduleDate, status, campaignTagsDetails = [], campaign_Title, campaignFlowDetails = [] } = useSelector((state) => state.campaigns);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [state, setState] = useState({
        schedulingTypeOption: 1,
        errors: {},
        schedulingDate: dayjs(),
    });

    const { schedulingTypeOption, errors, schedulingDate } = state;

    const date = new Date(schedulingDate);
    const formattedDate = `${String(date.getDate()).padStart(2, '0')} ${String(date.getMonth() + 1).padStart(2, '0')} ${date.getFullYear()} ${String(date.getHours()).padStart(2, '0')} ${String(date.getMinutes()).padStart(2, '0')} ${String(date.getSeconds()).padStart(2, '0')}`;

    const handleSingleSelectChange = (name, selectedValue) => {
        setState((prev) => ({
            ...prev,
            [name]: selectedValue,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    const handleDateChange = (value) => {
        setState((prev) => ({
            ...prev,
            schedulingDate: value,
        }));
    };

    const handleBack = () => {
        dispatch(setScheduleDate([{
            schedulingTypeOption: schedulingTypeOption,
            schedulingDate: schedulingDate
        }]))
        dispatch(setActiveBack());
    };


    const handleCreateCampaign = async (e) => {
        e.preventDefault();

        const validations = [
            { valid: !!schedulingTypeOption, message: "ScheduleType is required", field: "schedulingTypeOption" },
            ...(schedulingTypeOption === 2
                ? [{ valid: !!schedulingDate, message: "Date is required", field: "schedulingDate" }]
                : [])
        ];

        const newErrors = {};
        let isValid = true;

        for (let rule of validations) {
            if (!rule.valid) {
                setSnackbar((prev) => ({
                    ...prev,
                    open: true,
                    message: rule.message,
                    severity: "error",
                }));

                setState((prev) => ({
                    ...prev,
                    errors: { ...prev.errors, [rule.field]: rule.message },
                }));

                isValid = false;
                return;
            }
        }
        setState((prev) => ({ ...prev, errors: { ...prev.errors, ...newErrors } }));

        const details = campaignFlowDetails?.[0];
        const { selectedTemplate, selectedPlaceHolderValues, excelData } = details;

        // Excel recipients: rows that have a mobile column value. Each row keeps all its columns
        // so dynamic variables mapped to Excel fields get that row's value (backend fills them).
        const excelRows = excelData?.rows || [];
        const firstRow = excelRows[0] || {};
        const mobileKey = Object.keys(firstRow).find((k) => /^(mobile|phone|whatsapp)( ?(no|number))?$/i.test(String(k).trim()));
        const nameKey = Object.keys(firstRow).find((k) => /^name$/i.test(String(k).trim()));
        const excelRecipients = mobileKey
            ? excelRows
                .filter((row) => String(row[mobileKey] ?? "").replace(/\D/g, "").length >= 10)
                .map((row) => ({ mobile: String(row[mobileKey]), name: nameKey ? String(row[nameKey] ?? "") : "", data: row }))
            : [];
        const { includeTags, excludeTags } = campaignTagsDetails[0];

        const transformData = (arr) => {
            const groupMap = { head: "header" };

            return Object.values(
                arr.reduce((acc, item, idx) => {
                    const type = groupMap[item.type] || item.type;
                    if (!acc[type]) acc[type] = { type, parameters: [] };

                    acc[type].parameters.push(
                        item.inputType === "input"
                            ? { type: "text", text: item.value, mode: "static", key: "" }
                            : { type: "text", text: "", mode: "dynamic", key: item.value }
                    );

                    return acc;
                }, {})
            );
        };

        try {
            const result = await dispatch(
                createCampaignThunk({
                    credentials: {
                        "campaign_name": campaign_Title,
                        "template_id": selectedTemplate,
                        "notification_type": schedulingTypeOption,
                        "scheduled_time": schedulingTypeOption === 2 ? Math.floor(new Date(schedulingDate).getTime() / 1000) : moment().add(2, 'minute').unix(),
                        "tag_ids": includeTags.map((item) => item.value).toString(""),
                        "tags_is_not": excludeTags.map((item) => item.value).toString(""),
                        "template_variables": transformData(selectedPlaceHolderValues),
                        "excel_recipients": excelRecipients,
                    }
                })
            );

            if (result.meta.requestStatus === "fulfilled") {
                setSnackbar({
                    open: true,
                    message: result.payload.message,
                    severity: "success",
                });

                dispatch(getCampaignsThunk({ type: "getCampaigns" }));
                navigate(APP_ROUTES.CAMPAIGN.LIST);
            } else {
                setSnackbar({
                    open: true,
                    message: result.payload,
                    severity: "error",
                });
            }
        } catch {
            setSnackbar({
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            });
        }

        return isValid;
    };


    useEffect(() => {
        if (scheduleDate && scheduleDate?.length > 0) {
            const {
                schedulingTypeOption,
                schedulingDate
            } = scheduleDate[0];

            setState(prev => ({
                ...prev,
                schedulingTypeOption,
                schedulingDate
            }));
        } else {
            setState(prev => ({
                ...prev,
                schedulingTypeOption: 1,
            }));
        }
    }, [scheduleDate]);



    return (
        <div>
            <Card sx={stepCardSx}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <StepHeader icon={<ScheduleSendIcon sx={{ fontSize: 20 }} />} title="Schedule" subtitle="Send right away or pick a date and time" />
                    <Grid container spacing={2.5} alignItems="flex-end">
                        <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                            <FieldSelect
                                name="schedulingTypeOption"
                                label="When to send"
                                required
                                icon={<ScheduleSendIcon />}
                                placeholder="Select"
                                options={[
                                    { value: 1, label: "Immediately" },
                                    { value: 2, label: "Custom Date" },
                                ]}
                                value={schedulingTypeOption}
                                onChange={handleSingleSelectChange}
                                error={!!errors.schedulingTypeOption}
                            />
                        </Grid>

                        {schedulingTypeOption === 2 && (
                            <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                                <DateTimePickerField
                                    name="schedulingDate"
                                    label="Date & Time"
                                    required
                                    value={schedulingDate}
                                    onChange={handleDateChange}
                                    format="DD-MM-YYYY  HH:mm:ss A"
                                    error={!!errors.schedulingDate}
                                />
                            </Grid>
                        )}
                    </Grid>
                </CardContent>

                <CardActions sx={stepActionsSx}>
                    <Stack direction="row" spacing={1.5}>
                        <Button disabled={activeStep === 0} variant="outlined" onClick={handleBack} sx={backBtnSx}>
                            Back
                        </Button>
                        <div style={{ cursor: status === "loading" ? "not-allowed" : "pointer" }}>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={status === "loading"}
                                onClick={status === "loading" ? null : handleCreateCampaign}
                                sx={nextBtnSx}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: 14.5,
                                        color: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {status === "loading" && (
                                        <CircularProgress
                                            size={15}
                                            sx={{ mr: 1, color: "var(--white)" }}  // ✅ force white spinner
                                        />
                                    )}
                                    {status === "loading" ? "Submitting.." : "Submit"}
                                </Typography>
                            </Button>
                        </div>
                    </Stack>
                </CardActions>
            </Card>
            <SnackbarMessage open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} />
        </div>
    );
}